import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Link2, Mail, RefreshCw, Ban, CheckCircle2, Eye, PenLine,
  ShieldCheck, ShieldOff, MailWarning, Info,
} from "lucide-react";
import { api, formatDate } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { newRequestKey } from "@/lib/emailSend";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// Slice 2P1 Pass B · reusable contractor-facing sharing controls.
// Kept small and deterministic: no global store, no background jobs,
// no reminders. Drives the accepted Pass A endpoints directly and
// refreshes local state after every successful action.

const KIND_PATHS = {
  proposal: { pub: "p", api: "proposals" },
  contract: { pub: "c", api: "contracts" },
  // Slice 2P1 Pass C · Closeout Packet reuses the locked sharing lifecycle.
  // API path is the sharing-endpoint plural (`/closeouts/{id}/...`), which is
  // distinct from the closeout collection's doc-fetch path
  // (`/closeout-packets/{id}`). The parent's `onDocRefresh` handles the
  // doc fetch, so `paths.api` only feeds the sharing-endpoint URLs here.
  closeout: { pub: "co", api: "closeouts" },
};

const _labelKey = (s) => `sharing_card.state_label.${s || "private"}`;
const _leviKey  = (s) => `sharing_card.levi.${s || "private"}`;
const _histKey  = (event) => `sharing_card.history.event.${event}`;

const _fmt = (template, vars) => {
  if (!template || typeof template !== "string") return template;
  return template.replace(/\{(\w+)\}/g, (_, k) => (vars && vars[k] != null ? vars[k] : `{${k}}`));
};

const StatePill = ({ derivedState, t }) => {
  const label = t(_labelKey(derivedState));
  const tone = {
    private: "bg-[var(--jc-border)] text-[var(--jc-text)]",
    approved: "bg-amber-100 text-amber-900",
    shared: "bg-emerald-100 text-emerald-900",
    revoked: "bg-rose-100 text-rose-900",
    expired: "bg-slate-200 text-slate-800",
  }[derivedState] || "bg-[var(--jc-border)] text-[var(--jc-text)]";
  return (
    <span
      data-testid="sharing-state-pill"
      data-state={derivedState}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${tone}`}
    >
      <ShieldCheck size={12} className="opacity-70" /> {label}
    </span>
  );
};

const PreferenceAdvisory = ({ pref, t }) => {
  if (!pref) return null;
  const map = {
    no_customer_access: {
      key: "sharing_card.preference.no_customer_access",
      Icon: ShieldOff, tone: "bg-rose-50 border-rose-200 text-rose-900",
    },
    email_only: {
      key: "sharing_card.preference.email_only",
      Icon: Mail, tone: "bg-blue-50 border-blue-200 text-blue-900",
    },
    sms_only: {
      key: "sharing_card.preference.sms_only",
      Icon: MailWarning, tone: "bg-amber-50 border-amber-200 text-amber-900",
    },
    portal_and_email: {
      key: "sharing_card.preference.portal_and_email",
      Icon: Info, tone: "bg-slate-50 border-slate-200 text-slate-800",
    },
  };
  const spec = map[pref];
  if (!spec) return null; // use_business_default → no visible advisory
  const { Icon } = spec;
  return (
    <div
      data-testid={`sharing-preference-advisory-${pref}`}
      className={`flex items-start gap-2 rounded-md border px-3 py-2 text-xs ${spec.tone}`}
    >
      <Icon size={14} className="mt-0.5 shrink-0" />
      <span>{t(spec.key)}</span>
    </div>
  );
};

const HistoryList = ({ history, t }) => {
  const visible = (history || []).filter((h) => {
    const k = _histKey(h.event);
    return t(k) !== k; // hide events we don't display
  });
  if (!visible.length) {
    return (
      <div data-testid="sharing-history-empty" className="text-xs text-[var(--jc-text-soft)]">
        {t("sharing_card.history.empty")}
      </div>
    );
  }
  return (
    <ol data-testid="sharing-history-list" className="space-y-1.5 text-xs">
      {visible.map((h, i) => (
        <li key={i} data-testid={`sharing-history-item-${h.event}`} className="flex items-center justify-between gap-3">
          <span className="font-semibold text-[var(--jc-text)]">{t(_histKey(h.event))}</span>
          <span className="text-[var(--jc-text-soft)] font-mono-jc text-[10px]">{formatDate(h.at)}</span>
        </li>
      ))}
    </ol>
  );
};

const ConfirmDialog = ({ open, onOpenChange, title, body, confirmLabel, cancelLabel, onConfirm, busy, testid }) => (
  <AlertDialog open={open} onOpenChange={onOpenChange}>
    <AlertDialogContent data-testid={`${testid}-dialog`}>
      <AlertDialogHeader>
        <AlertDialogTitle>{title}</AlertDialogTitle>
        <AlertDialogDescription>{body}</AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel data-testid={`${testid}-cancel`} disabled={busy}>{cancelLabel}</AlertDialogCancel>
        <AlertDialogAction data-testid={`${testid}-confirm`} disabled={busy} onClick={onConfirm}>
          {confirmLabel}
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

export default function SharingCard({ kind, docId, doc, onDocRefresh }) {
  const { t } = useLang();
  const paths = KIND_PATHS[kind];
  const [share, setShare] = useState(null);
  const [loadErr, setLoadErr] = useState(false);
  const [busy, setBusy] = useState(null); // action key while a request is in flight
  const busyRef = useRef(null);
  const [dialog, setDialog] = useState(null); // { kind: "regenerate" | "revoke" | "approve_email" | "share_again" }

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get(`/${paths.api}/${docId}/share`);
      setShare(data);
      setLoadErr(false);
    } catch {
      setLoadErr(true);
    }
  }, [paths.api, docId]);

  useEffect(() => { refresh(); }, [refresh]);

  const setBusyBoth = (key) => { busyRef.current = key; setBusy(key); };
  const clearBusy   = () => { busyRef.current = null; setBusy(null); };

  const derivedState = share?.derived_state || share?.state || "private";
  const pref         = share?.customer_access_preference;
  const accessBlocked = pref === "no_customer_access";
  const token = share?.token;
  const publicUrl = token ? `${window.location.origin}/${paths.pub}/${token}` : "";
  const customerEmail = doc?.snapshot?.customer?.email || "";
  const signed = kind === "contract" && doc?.status === "signed";

  // ----- Actions ---------------------------------------------------------
  // Slice 2P1 Pass B · lifecycle correction. "Ready to Share" is a
  // contractor workflow state. It DOES NOT mint a customer link, does
  // not issue a URL, and does not imply the customer has received
  // anything. The customer link is issued only when the contractor
  // deliberately chooses Copy Link or Email Link — those actions are
  // what transition Ready to Share → Shared.
  const doApprove = async () => {
    if (busyRef.current || accessBlocked) return;
    setBusyBoth("approve");
    try {
      await api.post(`/${paths.api}/${docId}/approve-share`);
      toast.success(t("sharing_card.toast.approved"));
      await refresh();
      if (onDocRefresh) onDocRefresh();
    } catch {
      toast.error(t("sharing_card.toast.approve_failed"));
    } finally { clearBusy(); }
  };

  const doCopy = async () => {
    if (busyRef.current || accessBlocked) return;
    setBusyBoth("copy");
    try {
      // Lifecycle: from Ready to Share (approved, no token yet) the
      // contractor's deliberate Copy action mints the customer link
      // and transitions to Shared. From Shared we reuse the existing
      // token — share-link is idempotent server-side.
      let activeToken = token;
      if (!activeToken) {
        const { data } = await api.post(`/${paths.api}/${docId}/share-link`);
        activeToken = data?.token;
        if (!activeToken) throw new Error("no_token_after_share_link");
      }
      const url = `${window.location.origin}/${paths.pub}/${activeToken}`;
      await navigator.clipboard.writeText(url);
      toast.success(t("sharing_card.toast.copied"));
      await refresh();
      if (onDocRefresh) onDocRefresh();
    } catch {
      toast.error(t("sharing_card.toast.copy_failed"));
    } finally { clearBusy(); }
  };

  const doEmail = async ({ withConfirmShare = false } = {}) => {
    if (busyRef.current || accessBlocked) return;
    setBusyBoth("email");
    try {
      const request_key = newRequestKey();
      const payload = { request_key };
      if (withConfirmShare) payload.confirm_share = true;
      const { data } = await api.post(`/email/${kind}/${docId}`, payload);
      // Only claim success when the backend reports success.
      const delivered = data?.sent === true || data?.mock === true;
      if (delivered) {
        const key = data.mock ? "sharing_card.toast.emailed_mock" : "sharing_card.toast.emailed";
        toast.success(_fmt(t(key), { email: customerEmail || "—" }));
      } else {
        toast.error(t("sharing_card.toast.email_failed"));
      }
      await refresh();
      if (onDocRefresh) onDocRefresh();
    } catch (e) {
      // Never surface false "sent" — always show a clear error.
      toast.error(t("sharing_card.toast.email_failed"));
    } finally { clearBusy(); }
  };

  const doRegenerate = async () => {
    if (busyRef.current || accessBlocked) return;
    setBusyBoth("regenerate");
    try {
      await api.post(`/${paths.api}/${docId}/rotate-share`);
      toast.success(t("sharing_card.toast.regenerated"));
      await refresh();
      if (onDocRefresh) onDocRefresh();
    } catch {
      toast.error(t("sharing_card.toast.regenerate_failed"));
    } finally { clearBusy(); setDialog(null); }
  };

  const doRevoke = async () => {
    if (busyRef.current) return;
    setBusyBoth("revoke");
    try {
      await api.post(`/${paths.api}/${docId}/revoke-share`);
      toast.success(t("sharing_card.toast.revoked"));
      await refresh();
      if (onDocRefresh) onDocRefresh();
    } catch {
      toast.error(t("sharing_card.toast.revoke_failed"));
    } finally { clearBusy(); setDialog(null); }
  };

  const doShareAgain = async () => {
    // Lifecycle: from Revoked, Share Again transitions to Ready to Share
    // only. It does NOT re-issue a customer link automatically — the
    // contractor must then deliberately Copy Link or Email Link, which
    // is what transitions Ready to Share → Shared.
    if (busyRef.current || accessBlocked) return;
    setBusyBoth("share_again");
    try {
      await api.post(`/${paths.api}/${docId}/approve-share`);
      toast.success(t("sharing_card.toast.share_again_done"));
      await refresh();
      if (onDocRefresh) onDocRefresh();
    } catch {
      toast.error(t("sharing_card.toast.approve_failed"));
    } finally { clearBusy(); setDialog(null); }
  };

  // ----- Rendering -------------------------------------------------------
  if (loadErr) {
    return (
      <div data-testid="sharing-card-load-error" className="jc-card p-4 border border-rose-200 text-sm text-rose-800">
        {t("sharing_card.load_error")}
      </div>
    );
  }
  if (!share) {
    return (
      <div data-testid="sharing-card-loading" className="jc-card p-4 text-xs text-[var(--jc-text-soft)]">
        …
      </div>
    );
  }

  // Access is disabled → show the advisory in place of controls.
  if (accessBlocked) {
    return (
      <section
        data-testid="sharing-card"
        data-kind={kind}
        data-state={derivedState}
        className="jc-card p-4 sm:p-5 space-y-3"
      >
        <div className="flex items-center justify-between gap-3">
          <div className="font-display text-base">{t("sharing_card.heading")}</div>
          <StatePill derivedState={derivedState} t={t} />
        </div>
        <PreferenceAdvisory pref={pref} t={t} />
      </section>
    );
  }

  // Copy/Email are the contractor's deliberate mint-and-share actions
  // from Ready to Share, and reuse the existing link from Shared. The
  // buttons are enabled whenever the state allows the transition — the
  // presence of a live token is not required at Ready to Share.
  const canCopy = ["approved", "shared"].includes(derivedState);
  const canEmail = ["approved", "shared"].includes(derivedState);
  const isPrivate = derivedState === "private";
  const isRevoked = derivedState === "revoked";
  const isExpired = derivedState === "expired";

  return (
    <section
      data-testid="sharing-card"
      data-kind={kind}
      data-state={derivedState}
      className="jc-card p-4 sm:p-5 space-y-4"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="font-display text-base">{t("sharing_card.heading")}</div>
          <div className="text-xs text-[var(--jc-text-soft)] mt-1">
            {t("sharing_card.subheading")}
          </div>
        </div>
        <StatePill derivedState={derivedState} t={t} />
      </div>

      <PreferenceAdvisory pref={pref} t={t} />

      {/* Levi inline guidance — deterministic, no API */}
      <div
        data-testid="sharing-levi-note"
        className="rounded-md border border-[var(--jc-border)] bg-[var(--jc-bg)] px-3 py-2 text-xs leading-relaxed"
      >
        <span className="font-semibold text-[var(--jc-text)]">{t("sharing_card.levi_intro")}</span>{" "}
        <span className="text-[var(--jc-text-soft)]">{t(_leviKey(derivedState))}</span>
      </div>

      {/* Meta row — expires / viewed / signed */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
        <div data-testid="sharing-meta-expires" className="flex items-center gap-1.5 text-[var(--jc-text-soft)]">
          <span className="font-semibold">{t("sharing_card.meta.expires_prefix")}:</span>
          <span data-testid="sharing-expires-at" className="font-mono-jc">
            {share.expires_at ? formatDate(share.expires_at) : t("sharing_card.meta.expires_none")}
          </span>
        </div>
        <div data-testid="sharing-meta-viewed" className="flex items-center gap-1.5">
          <Eye size={12} className="text-[var(--jc-text-soft)]" />
          <span className={share.first_viewed_at ? "text-emerald-800 font-semibold" : "text-[var(--jc-text-soft)]"}>
            {share.first_viewed_at ? t("sharing_card.meta.viewed_yes") : t("sharing_card.meta.viewed_no")}
          </span>
        </div>
        {kind === "contract" && (
          <div data-testid="sharing-meta-signed" className="flex items-center gap-1.5">
            <PenLine size={12} className="text-[var(--jc-text-soft)]" />
            <span className={signed ? "text-emerald-800 font-semibold" : "text-[var(--jc-text-soft)]"}>
              {signed ? t("sharing_card.meta.signed_yes") : t("sharing_card.meta.signed_no")}
            </span>
          </div>
        )}
      </div>

      {/* Actions — depend on derived state */}
      <div className="flex flex-wrap items-center gap-2">
        {isPrivate && (
          <>
            <button
              data-testid="sharing-action-approve"
              onClick={doApprove}
              disabled={Boolean(busy)}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-md jc-btn-blue disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <CheckCircle2 size={14} />
              {busy === "approve" ? t("sharing_card.action_busy.approve") : t("sharing_card.action.approve")}
            </button>
            <button
              data-testid="sharing-action-approve-and-email"
              onClick={() => setDialog({ kind: "approve_email" })}
              disabled={Boolean(busy)}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-md border border-[var(--jc-border-strong)] hover:bg-[var(--jc-bg)] disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <Mail size={14} />
              {t("sharing_card.action.approve_and_email")}
            </button>
          </>
        )}

        {(derivedState === "approved" || derivedState === "shared") && (
          <>
            <button
              data-testid="sharing-action-copy"
              onClick={doCopy}
              disabled={!canCopy || Boolean(busy)}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-md border border-[var(--jc-border-strong)] hover:bg-[var(--jc-bg)] disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <Link2 size={14} />
              {busy === "copy" ? t("sharing_card.action_busy.copy") : t("sharing_card.action.copy")}
            </button>
            <button
              data-testid="sharing-action-email"
              onClick={() => doEmail()}
              disabled={!canEmail || Boolean(busy)}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-md border border-[var(--jc-border-strong)] hover:bg-[var(--jc-bg)] disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <Mail size={14} />
              {busy === "email" ? t("sharing_card.action_busy.email") : t("sharing_card.action.email")}
            </button>
            <button
              data-testid="sharing-action-regenerate"
              onClick={() => setDialog({ kind: "regenerate" })}
              disabled={Boolean(busy)}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-md border border-[var(--jc-border-strong)] hover:bg-[var(--jc-bg)] disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <RefreshCw size={14} />
              {t("sharing_card.action.regenerate")}
            </button>
            <button
              data-testid="sharing-action-revoke"
              onClick={() => setDialog({ kind: "revoke" })}
              disabled={Boolean(busy)}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-md border border-rose-300 text-rose-800 hover:bg-rose-50 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <Ban size={14} />
              {t("sharing_card.action.revoke")}
            </button>
          </>
        )}

        {isRevoked && (
          <button
            data-testid="sharing-action-share-again"
            onClick={() => setDialog({ kind: "share_again" })}
            disabled={Boolean(busy)}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-md jc-btn-blue disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <CheckCircle2 size={14} />
            {t("sharing_card.action.share_again")}
          </button>
        )}

        {isExpired && (
          <>
            <button
              data-testid="sharing-action-regenerate"
              onClick={() => setDialog({ kind: "regenerate" })}
              disabled={Boolean(busy)}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-md jc-btn-blue disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <RefreshCw size={14} />
              {t("sharing_card.action.regenerate")}
            </button>
            <button
              data-testid="sharing-action-revoke"
              onClick={() => setDialog({ kind: "revoke" })}
              disabled={Boolean(busy)}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-md border border-rose-300 text-rose-800 hover:bg-rose-50 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <Ban size={14} />
              {t("sharing_card.action.revoke")}
            </button>
          </>
        )}
      </div>

      {/* Active link display — visible only in the Shared state, i.e.
          after the contractor has deliberately issued the customer
          link via Copy Link or Email Link. Ready to Share has no
          active URL because nothing has yet been shared. */}
      {publicUrl && derivedState === "shared" && (
        <div className="rounded-md border border-[var(--jc-border)] bg-white px-3 py-2 text-xs break-all">
          <div className="text-[10px] uppercase tracking-wider text-[var(--jc-text-soft)] mb-0.5">
            {t("sharing_card.action.copy")}
          </div>
          <code data-testid="sharing-active-url" className="font-mono-jc text-[var(--jc-text)]">
            {publicUrl}
          </code>
        </div>
      )}

      {/* History */}
      <div className="pt-2 border-t border-[var(--jc-border)]">
        <div className="text-xs font-semibold text-[var(--jc-text)] mb-2">
          {t("sharing_card.history.heading")}
        </div>
        <HistoryList history={share.history} t={t} />
      </div>

      {/* Confirmation dialogs */}
      <ConfirmDialog
        open={dialog?.kind === "approve_email"}
        onOpenChange={(o) => (o ? null : setDialog(null))}
        title={t("sharing_card.confirm.approve_email_title")}
        body={_fmt(t("sharing_card.confirm.approve_email_body"), { email: customerEmail || "—" })}
        confirmLabel={t("sharing_card.confirm.confirm")}
        cancelLabel={t("sharing_card.confirm.cancel")}
        busy={Boolean(busy)}
        testid="sharing-confirm-approve-email"
        onConfirm={async () => { await doEmail({ withConfirmShare: true }); setDialog(null); }}
      />
      <ConfirmDialog
        open={dialog?.kind === "regenerate"}
        onOpenChange={(o) => (o ? null : setDialog(null))}
        title={t("sharing_card.confirm.regenerate_title")}
        body={t("sharing_card.confirm.regenerate_body")}
        confirmLabel={t("sharing_card.confirm.confirm")}
        cancelLabel={t("sharing_card.confirm.cancel")}
        busy={Boolean(busy)}
        testid="sharing-confirm-regenerate"
        onConfirm={doRegenerate}
      />
      <ConfirmDialog
        open={dialog?.kind === "revoke"}
        onOpenChange={(o) => (o ? null : setDialog(null))}
        title={t("sharing_card.confirm.revoke_title")}
        body={t("sharing_card.confirm.revoke_body")}
        confirmLabel={t("sharing_card.confirm.confirm")}
        cancelLabel={t("sharing_card.confirm.cancel")}
        busy={Boolean(busy)}
        testid="sharing-confirm-revoke"
        onConfirm={doRevoke}
      />
      <ConfirmDialog
        open={dialog?.kind === "share_again"}
        onOpenChange={(o) => (o ? null : setDialog(null))}
        title={t("sharing_card.confirm.share_again_title")}
        body={t("sharing_card.confirm.share_again_body")}
        confirmLabel={t("sharing_card.confirm.confirm")}
        cancelLabel={t("sharing_card.confirm.cancel")}
        busy={Boolean(busy)}
        testid="sharing-confirm-share-again"
        onConfirm={doShareAgain}
      />
    </section>
  );
}
